// libraries
import React, { useEffect, useState } from 'react'
import clsx from 'clsx'

// icons
import { CaretDown } from '../Svg/Others/Icons'

interface BreadcrumbsProps {
	className?: string
	breadcrumbs: Array<{
		name: string
		href?: string
	}>
}

export default function Breadcrumbs({
	className,
	breadcrumbs
}: BreadcrumbsProps) {

  const [breadcrumbColor, setBreadcrumbColor] = useState('transparent')

  useEffect(() => {
		const updateBreadcrumbColor = () => {
			const sections = document.querySelectorAll('section')
			let currentBgColor = ''

			sections.forEach(section => {
				const rect = section.getBoundingClientRect()
				const isMobile = window.matchMedia('(max-width: 767px)').matches
				const measure = isMobile ? 123 : 162

				if (rect.top <= measure && rect.bottom >= 0) {
					const sectionBgColor = window.getComputedStyle(section).backgroundColor

					if (sectionBgColor !== 'rgba(0, 0, 0, 0)' && sectionBgColor !== 'transparent') {
						currentBgColor = sectionBgColor
					}
				}
			})

			// ff no valid section background color is found, check <main>
			if (!currentBgColor || currentBgColor === 'rgba(0, 0, 0, 0)' || currentBgColor === 'transparent') {
				const mainElement = document.getElementById('main')

				if (mainElement) {
					const mainBgColor = window.getComputedStyle(mainElement).backgroundColor

					if (mainBgColor !== 'rgba(0, 0, 0, 0)' && mainBgColor !== 'transparent') {
						currentBgColor = mainBgColor
					}
				}
			}
	
			// if no valid background is found in <main>, check <body>
			if (!currentBgColor || currentBgColor === 'rgba(0, 0, 0, 0)' || currentBgColor === 'transparent') {
				const bodyBgColor = window.getComputedStyle(document.body).backgroundColor

				currentBgColor = bodyBgColor !== 'rgba(0, 0, 0, 0)' && bodyBgColor !== 'transparent'
					? bodyBgColor
					: 'transparent'
			}
	
			// apply the determined background color
			setBreadcrumbColor(currentBgColor);
		}

		const nextSectionColor = () => {
			const sections = document.querySelectorAll('section')
			let nextSectionIndex = 0


			Array.from(sections).forEach((section, index) => {
				const rect = section.getBoundingClientRect()

				if (rect.top <= 0) {
					nextSectionIndex = index + 1
				}
			})

			if (nextSectionIndex < sections.length) {
				const nextSection = sections[nextSectionIndex]
				const computedStyle = window.getComputedStyle(nextSection)
				const currentBgColor = computedStyle.backgroundColor
				setBreadcrumbColor(currentBgColor)
			} else {
				setBreadcrumbColor('transparent')
			}
		}

		window.addEventListener('scroll', updateBreadcrumbColor)
		// updateBreadcrumbColor();

		let lastScrollTop = 0
    
        const handleScroll = () => {
			const breadcrumbs = document.querySelector('.breadcrumbs__section')
			if (!breadcrumbs) return

			// show / hide header based on scroll direction
			const isMobile = window.matchMedia('(max-width: 767px)').matches
			let currentScroll = 0

			if (isMobile) {
				currentScroll = window.pageYOffset || document.documentElement.scrollTop
			}

			// scrolling down / up
			if (currentScroll > lastScrollTop) {
				breadcrumbs.classList.add('breadcrumbs__section--hidden')
			} else {
				breadcrumbs.classList.remove('breadcrumbs__section--hidden')
			}

			// Prevent negative scroll values
			lastScrollTop = currentScroll <= 0 ? 0 : currentScroll
        }

        window.addEventListener('scroll', handleScroll)

  	}, [])

	const rgbToHex = (rgb: string) => {
		const rgbValues = rgb.match(/\d+/g)
		return rgbValues ? `#${rgbValues.map(x => parseInt(x).toString(16).padStart(2, '0')).join('')}`.toUpperCase() : ''
	}

	const isActiveColor = [
		'#001F42',
		'#002855',
		'rgb(0, 31, 66)'
	].includes(breadcrumbColor) || ['#001F42', '#002855'].includes(rgbToHex(breadcrumbColor))

	useEffect(() => {
		const dropdownIcons = document.querySelectorAll('.breadcrumbs__dropdown-icon')

		dropdownIcons.forEach(icon => {
			icon.addEventListener('click', handleDropdownClick)
		})

		return () => {
			dropdownIcons.forEach(icon => {
				icon.removeEventListener('click', handleDropdownClick)
			})
		}
	}, [])

	function handleDropdownClick(event) {
		const dropdownIcon = event.currentTarget
		const dropdown = dropdownIcon.closest('.breadcrumbs__list')
		const dropdownArea = dropdown.querySelector('.breadcrumbs__dropdown-area')
	
		dropdownArea.classList.toggle('breadcrumbs__dropdown-area--show')
		dropdownIcon.setAttribute('aria-expanded', dropdownArea.classList.contains('show') ? 'true' : 'false')
	}

	return breadcrumbs.length > 0 ? (
		<section className='section breadcrumbs__section'>
			<div className='breadcrumbs__color' style={{ backgroundColor: breadcrumbColor }}>
				<div className={clsx('grid-container')}>
					<div className="grid-row">
						<div className="c-12">

							<div className={clsx('breadcrumbs', isActiveColor && 'breadcrumbs--light', className)}>

								<div className='breadcrumbs__list' itemScope itemType='http://schema.org/BreadcrumbList'>

									{/* this is the dropdown for mobile */}
									<span className='breadcrumbs__dropdown' itemProp='itemListElement'>
										<span className='breadcrumbs__dropdown-wrapper'>
											
											<span className='text-light-bg-tertiary'>
												...
											</span>

											<button className='breadcrumbs__dropdown-icon' aria-expanded="false" aria-label='Show more'>
												<CaretDown />
											</button>

										</span>
									</span>

									{/* this is the list in mobile, that renders the list, except not the last li */}
									<span className='breadcrumbs__dropdown-area'>
										{breadcrumbs.map((item, i) => (
											<span
												className='li'
												key={i}
												itemProp='itemListElement'
												itemScope
												itemType='http://schema.org/ListItem'
											>
												{item.href ? (
													<>
														<a href={item.href} className='regular-link' itemProp='item'>
															<span itemProp='name'>
																{item.name}
															</span>
														</a>

														<meta itemProp='position' content={`${i + 1}`} />
													</>
												) : (
													<>
														<span itemProp='item'>
															<span itemProp='name'>
																{item.name}
															</span>
														</span>
														
														<meta itemProp='position' content={`${i + 1}`} />
														<meta itemProp='currentpage' content='true' />
													</>
												)}

												<span className='breadcrumbs__separator'>/</span>

											</span>
										))}
									</span>

									{/* this is the desktop version */}
									{breadcrumbs.map((item, i) => (
										<span className='breadcrumbs__item' key={i}>
											<span
												className='li'
												key={i}
												itemProp='itemListElement'
												itemScope
												itemType='http://schema.org/ListItem'
											>
												{item.href ? (
													<>
														<a href={item.href} className='regular-link' itemProp='item'>
															<span itemProp='name'>
																{item.name}
															</span>
														</a>
														
														<meta itemProp='position' content={`${i + 1}`} />
													</>
												) : (
													<>
														<span itemProp='item'>
															<span itemProp='name'>
																{item.name}
															</span>
														</span>
														
														<meta itemProp='position' content={`${i + 1}`} />
														<meta itemProp='currentpage' content='true' />
													</>
												)}

												<span className='breadcrumbs__separator'>/</span>

											</span>
										</span>
									))}

								</div>

							</div>

						</div>
					</div>
				</div>
			</div>
		</section>
	) : <></>
}